export const seasonLabel = name => {
  const text = String(name || "");
  if (/^\d{4}$/.test(text)) { const year = Number(text); return `${year-1}/${String(year).slice(-2)}`; }
  return text.replace(/^(\d{4})\/(\d{2})(\d{2})$/, "$1/$3");
};
export function evolutionMatrix(records, leagues) {
  const merged = new Map();
  const allowed = new Set(leagues.map(l => Number(l.league_id)));
  records = records.filter(r => allowed.has(Number(r.league_id)));
  for (const r of records) {
    const key = `${r.league_id}:${r.season_id}`,
      previous = merged.get(key);
    if (!previous) {
      merged.set(key, { ...r, comparison_season: seasonLabel(r.season_name) });
      continue;
    }
    const actual = r.source === "base" ? r.actual : previous.actual;
    merged.set(key, {
      ...previous,
      ...(r.source === "personal"
        ? { expected: r.expected, comparison_actual: r.comparison_actual, comparison_kind: r.comparison_kind, expected_provenance: r.expected_provenance, transitions: r.transitions }
        : {}),
      actual,
    });
  }
  const rows = leagues.map((l) => ({
    id: l.league_id,
    name: l.league_name,
    league: { ...l, id: l.league_id, name: l.league_name },
    records: [...merged.values()].filter((r) => r.league_id === l.league_id),
  }));
  const seasons = new Map();
  for (const r of [
    ...records.filter((r) => r.actual || r.expected),
    ...leagues.filter(
      (l) =>
        Number(String(l.season_name).slice(0, 4)) <= new Date().getFullYear()
    ),
  ]) {
    const key = seasonLabel(r.season_name);
    if (!key) continue;
    const year = Number(key.match(/\d{4}/)?.[0] || 0);
    const date = year;
    const previous = seasons.get(key);
    if (!previous || date > previous.order)
      seasons.set(key, { key, label: key, order: date });
  }
  const collisions = rows.flatMap(row => {
    const grouped = new Map();
    for (const record of row.records) { const label = seasonLabel(record.season_name); grouped.set(label, [...(grouped.get(label) || []), record.season_id]); }
    return [...grouped].filter(([,ids]) => new Set(ids).size > 1).map(([season,ids]) => ({league_id: row.id, season, season_ids: ids}));
  });
  return {
    rows, collisions,
    seasons: [...seasons.values()].sort(
      (a, b) => b.order - a.order || b.key.localeCompare(a.key)
    ),
  };
}
const aggregate = (values) => {
  const valid = values.filter(
    (v) => v && Number.isFinite(v.numerator) && v.denominator > 0
  );
  const denominator = valid.reduce((n, v) => n + v.denominator, 0);
  return {
    value: denominator
      ? valid.reduce((n, v) => n + v.numerator, 0) / denominator
      : null,
    games: valid.reduce((n, v) => n + (v.games || 0), 0),
  };
};
export function evolutionValue(records, metric, mode, season) {
  const counts = new Map();
  for (const r of records) {const key = `${r.league_id}:${seasonLabel(r.season_name)}`; counts.set(key, (counts.get(key)||0)+1);}
  const selected = records.filter(r => counts.get(`${r.league_id}:${seasonLabel(r.season_name)}`) === 1).filter(
    (r) => !season || seasonLabel(r.season_name) === season
  );
  if (["promoted", "relegated"].includes(metric)) {
    const values = selected.map(r => r.transitions?.[metric]);
    return {...aggregate(values), teams: values.reduce((n,v)=>n+(v?.teams || 0),0)};
  }
  if (mode !== "difference")
    return aggregate(selected.map((r) => r[mode]?.[metric]));
  const paired = selected.filter(
    (r) =>
      r.expected?.[metric] &&
      r.comparison_actual?.[metric] &&
      (r.comparison_kind === "historical" || r.expected[metric].games === r.comparison_actual[metric].games)
  );
  const expected = aggregate(paired.map((r) => r.expected[metric])),
    actual = aggregate(paired.map((r) => r.comparison_actual[metric]));
  return {
    value:
      actual.value == null || expected.value == null
        ? null
        : actual.value - expected.value,
    games: expected.games,
    actualGames: actual.games,
  };
}
export function sortEvolutionRows(rows, seasons, metric, mode, sort) {
  return [...rows].sort((a, b) => {
    if (sort.key === "name")
      return a.name.localeCompare(b.name) * (sort.direction === "asc" ? 1 : -1);
    const x = evolutionValue(
      a.records,
      metric,
      mode,
      sort.key === "long" ? null : sort.key
    ).value;
    const y = evolutionValue(
      b.records,
      metric,
      mode,
      sort.key === "long" ? null : sort.key
    ).value;
    if (x == null) return y == null ? a.name.localeCompare(b.name) : 1;
    if (y == null) return -1;
    return (
      (x - y) * (sort.direction === "asc" ? 1 : -1) ||
      a.name.localeCompare(b.name)
    );
  });
}
